// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect, useCallback } from 'react';
import { getIdToken } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function ArchivePanel({ tenantId, tenantName }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthsToArchive, setMonthsToArchive] = useState(1);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [message, setMessage] = useState(null);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/archive-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load archive info');
      setInfo(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  async function handleArchive(archiveAll = false) {
    const count = archiveAll ? info.totalMonths : monthsToArchive;
    const monthNames = archiveAll
      ? info.months.map((m) => m.month).join(', ')
      : info.months.slice(0, monthsToArchive).map((m) => m.month).join(', ');
    const msg = archiveAll
      ? `Archive ALL ${info.totalRecords} records across ${info.totalMonths} months (${monthNames})?`
      : `Archive ${count} oldest month(s) (${monthNames})? This removes them from insights.`;

    if (!window.confirm(msg)) return;

    setArchiving(true);
    setMessage(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(archiveAll ? { archiveAll: true } : { monthsToArchive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Archive failed');
      setMessage(`✓ Archived ${data.recordCount} records (${data.monthsArchived.join(', ')})`);
      loadInfo();
    } catch (err) {
      setError(err.message);
    } finally {
      setArchiving(false);
    }
  }

  async function handleRestore(archiveId) {
    if (!window.confirm('Restore this archived data? Records will reappear in insights.')) return;
    setRestoring(archiveId);
    setMessage(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ archiveId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Restore failed');
      setMessage(`✓ Restored ${data.restoredCount} records`);
      loadInfo();
    } catch (err) {
      setError(err.message);
    } finally {
      setRestoring(null);
    }
  }

  if (loading) return <div className="archive-panel"><p style={{ color: '#888' }}>Loading archive info…</p></div>;
  if (error) return <div className="archive-panel"><p style={{ color: '#c62828' }}>{error}</p></div>;
  if (!info) return null;

  return (
    <div className="archive-panel">
      <h3 className="archive-panel__title">Data Management</h3>

      {message && <div className="archive-panel__message">{message}</div>}

      {/* Active data summary */}
      <div className="archive-panel__summary">
        <div className="archive-panel__stat">
          <span className="archive-panel__stat-value">{info.totalMonths}</span>
          <span className="archive-panel__stat-label">months of active data</span>
        </div>
        <div className="archive-panel__stat">
          <span className="archive-panel__stat-value">{info.totalRecords}</span>
          <span className="archive-panel__stat-label">total records</span>
        </div>
      </div>

      {/* Month breakdown */}
      {info.months.length > 0 && (
        <div className="archive-panel__months">
          <p className="archive-panel__months-label">Active months:</p>
          <div className="archive-panel__month-list">
            {info.months.map((m) => (
              <span key={m.month} className="archive-panel__month-chip">
                {m.month} <small>({m.count})</small>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Archive controls */}
      {info.totalMonths > 0 && (
        <div className="archive-panel__controls">
          <div className="archive-panel__control-row">
            <label>
              Archive oldest
              <select
                value={monthsToArchive}
                onChange={(e) => setMonthsToArchive(Number(e.target.value))}
                className="archive-panel__select"
              >
                {Array.from({ length: info.totalMonths }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n} month{n > 1 ? 's' : ''} ({info.months.slice(0, n).map((m) => m.month).join(', ')})
                  </option>
                ))}
              </select>
            </label>
            <button
              className="admin-btn admin-btn--primary"
              onClick={() => handleArchive(false)}
              disabled={archiving}
            >
              {archiving ? 'Archiving…' : 'Archive'}
            </button>
          </div>
          {info.totalMonths > 1 && (
            <button
              className="admin-btn admin-btn--small admin-btn--secondary"
              onClick={() => handleArchive(true)}
              disabled={archiving}
              style={{ marginTop: 8 }}
            >
              Archive all data
            </button>
          )}
        </div>
      )}

      {/* Archive history */}
      {info.archives.length > 0 && (
        <div className="archive-panel__history">
          <p className="archive-panel__months-label">Archive history:</p>
          {info.archives.map((a) => (
            <div key={a.archiveId} className="archive-panel__archive-item">
              <div className="archive-panel__archive-info">
                <strong>{a.monthsArchived.join(', ')}</strong>
                <span>{a.recordCount} records · archived {new Date(a.archivedAt).toLocaleDateString()}</span>
              </div>
              <button
                className="admin-btn admin-btn--small admin-btn--secondary"
                onClick={() => handleRestore(a.archiveId)}
                disabled={restoring === a.archiveId}
              >
                {restoring === a.archiveId ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
